/**
 * Trial Expired Banner Component
 * Zeigt eine Vollbild-Meldung wenn der Trial abgelaufen ist
 */

'use client';

import { useFeatureAccess } from '@/hooks/useFeatureAccess';
import { useTranslations } from 'next-intl';
import { usePathname } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { AlertTriangle, Clock, ArrowRight, Sparkles } from 'lucide-react';
import Link from 'next/link';

export function TrialExpiredBanner() {
  const t = useTranslations('admin.ui');
  const { isTrialExpired, storefrontBlocked, upgradePlan, loading } = useFeatureAccess();
  const pathname = usePathname();

  // Nicht anzeigen auf der Subscription-Seite (sonst kann man nicht upgraden!)
  const isSubscriptionPage = pathname === '/admin/subscription' || pathname.startsWith('/admin/subscription/');

  // Nicht anzeigen während des Ladens, wenn Trial nicht abgelaufen, oder auf Subscription-Seite
  if (loading || !isTrialExpired || isSubscriptionPage) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <Card className="mx-4 max-w-lg overflow-hidden border-0 shadow-2xl">
        {/* Header */}
        <div className="bg-blue-600 px-8 py-10 text-center text-white">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-white/20 backdrop-blur-sm">
            <AlertTriangle className="h-8 w-8" />
          </div>
          <h1 className="mb-2 text-2xl font-bold">{t('trialExpiredTitle')}</h1>
          <p className="text-red-100">
            {t('trialExpiredSubtitle')}
          </p>
        </div>

        <CardContent className="p-8">
          {/* Status */}
          <div className="mb-6 space-y-3">
            <div className="flex items-center gap-3 rounded-lg bg-red-50 p-4">
              <Clock className="h-5 w-5 text-red-600" />
              <div>
                <p className="font-medium text-red-900">{t('bookingPageDisabled')}</p>
                <p className="text-sm text-red-700">
                  {t('bookingPageDisabledHint')}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-3 rounded-lg bg-orange-50 p-4">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              <div>
                <p className="font-medium text-orange-900">{t('adminRestricted')}</p>
                <p className="text-sm text-orange-700">
                  {t('adminRestrictedHint')}
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="space-y-3">
            <Button
              asChild
              size="lg"
              className="w-full bg-blue-600 hover:from-indigo-700 hover:to-purple-700"
            >
              <Link href="/admin/subscription">
                <Sparkles className="mr-2 h-5 w-5" />
                {t('upgradeNowContinue')}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Link>
            </Button>

            <p className="text-center text-xs text-gray-500">
              {upgradePlan
                ? t('recommendedPlan', { plan: upgradePlan.charAt(0).toUpperCase() + upgradePlan.slice(1) })
                : t('chooseSuitablePlan')}
            </p>
          </div>

          {/* Benefits Reminder */}
          <div className="mt-6 border-t pt-6">
            <p className="mb-3 text-center text-sm font-medium text-gray-700">
              {t('upgradeBenefitsTitle')}
            </p>
            <ul className="space-y-2 text-sm text-gray-600">
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                {t('benefitUnlimitedBooking')}
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                {t('benefitCalendarSync')}
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                {t('benefitEmailNotifications')}
              </li>
              <li className="flex items-center gap-2">
                <span className="text-green-500">✓</span>
                {t('benefitAllPremium')}
              </li>
            </ul>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
